// Centralized message catalog
// Keep values ASCII when possible to avoid editor/encoding surprises

export const MESSAGES = {
  en: {
    nav: {
      home: "HOME",
      about: "ABOUT",
      services: "SERVICES",
      portfolio: "PORTFOLIO",
      projects: "PROJECTS",
      contacts: "CONTACT",
      toggleMenuAria: "Toggle menu",
      switchToItalianAria: "Switch to Italian",
      switchToEnglishAria: "Switch to English",
      darkModeToLightAria: "Switch to light mode",
      darkModeToDarkAria: "Switch to dark mode",
      localeIt: "IT",
      localeEn: "EN",
      brand: "YURII SUPRUN",
      tagline: "Digital Growth Partner",
    },
    seo: {
      home: {
        title: "Home - Yurii Suprun",
        description:
          "Yurii Suprun - Digital Growth Partner helping businesses increase visibility, inquiries, and build a professional online presence that converts.",
        keywords: "digital growth partner, web development, online presence, Yurii Suprun",
      },
       about: {
         title: "About Me - Yurii Suprun",
         description:
           "Learn more about Yurii Suprun, a software engineer skilled in full-stack development and cloud technologies.",
         keywords: "about, software engineer, Yurii Suprun, portfolio",
       },
       services: {
         title: "Services - Yurii Suprun",
         description:
           "Explore the services I offer including software development, consulting, and technical solutions.",
         keywords: "services, software development, consulting, Yurii Suprun",
       },
       projects: {
        title: "Projects - Yurii Suprun",
        description:
          "Explore projects by Yurii Suprun including GitHub repositories and web development works.",
        keywords: "projects, GitHub, software development, Yurii Suprun",
      },
      contacts: {
        title: "Contacts - Yurii Suprun",
        description:
          "Get in touch with Yurii Suprun for collaboration or project inquiries.",
        keywords: "contact, email, Yurii Suprun, collaboration",
      },
      portfolio: {
        title: "Portfolio - Yurii Suprun",
        description:
          "Explore selected works and projects showcasing my expertise in full-stack development and technical solutions.",
        keywords: "portfolio, projects, software development, Yurii Suprun",
      },
      privacy: {
        title: "Privacy Policy - Yurii Suprun",
        description: "Information about cookie usage and privacy preferences.",
        keywords: "privacy policy, cookies, Yurii Suprun",
      },
    },
    hero: {
      headlineLines: ["Build Your Digital", "Presence. Drive Growth."],
      headline: "Build Your Digital Presence. Drive Growth.",
      subtitle: "I help businesses create digital solutions that attract customers, increase visibility, and generate measurable results. From AI-powered automations to conversion-focused websites.",
      ctaProjects: "View Portfolio",
      ctaContact: "Tell Me Your Project",
      
      // Social Proof
      experience: "8+ Years Experience",
      projects: "15+ Projects Completed",
      industries: "Startups & SMEs",
      
      // Legacy keys kept for backwards-compat if referenced elsewhere.
      rolePrefix: "Hi, I'm Yurii, a software engineer",
      roleSuffix: " who builds ",
      typewriterWords: ["interactive", "scalable", "engaging"],
      roleSuffixEnd: "solutions",
      imageAlt: "Yurii Suprun - Software Engineer",
      links: {
        linkedin: "LinkedIn",
        github: "GitHub",
        email: "iursuprun@gmail.com",
      },
    },
    featured: {
      kicker: "FEATURED PROJECTS",
      title: "Things I've built",
      analytics: {
        title: "Legal Studio",
        description:
          "Professional website showcasing legal services with a modern design and client-focused interface.",
        tags: ["React", "TypeScript", "Chart.js"],
      },
      task: {
        title: "Task Manager",
        description:
          "A productivity app to organize tasks, set priorities and track progress.",
        tags: ["Next.js", "Tailwind CSS", "Prisma"],
      },
      ecommerce: {
        title: "E-commerce Platform",
        description:
          "Full-stack e-commerce platform with product catalog, cart and payments.",
        tags: ["Node.js", "Express", "PostgreSQL"],
      },
    },
    about: {
      title: "About Me",
      description: [
        "I'm Yurii, a Digital Growth Partner specializing in helping businesses build powerful online presence that attracts customers and drives measurable growth.",
        "I combine strategic thinking with technical expertise to create digital solutions that aren't just technically sound—they're designed to grow your business. Whether it's increasing visibility with SEO, generating qualified inquiries through high-conversion sites, capturing leads with AI assistants, or automating repetitive tasks, I focus on outcomes that matter.",
        "My approach is collaborative and results-driven. I work closely with you to understand your business goals, then architect solutions that deliver real value—whether that's a professional website, an AI-powered chatbot, a conversion-focused landing page, or a complete digital transformation.",
      ],
    },
    services: {
      title: "Services",
      description: "Result-oriented solutions",
      items: [
        {
          name: "Voice AI Assistants",
          description: "I create Voice AI assistants capable of answering customer questions, collecting contacts, qualifying leads, booking appointments and providing 24/7 assistance.",
          details: [
            "Works even when your office is closed.",
          ],
        },
        {
          name: "Professional Websites",
          description: "I create modern, fast and responsive websites designed to transform visitors into customers. Each project is optimized to deliver an excellent experience on desktop, tablets and smartphones.",
          details: [
            "Ideal for: companies, professionals, shops and local businesses.",
          ],
        },
        {
          name: "High-Conversion Landing Pages",
          description: "I create landing pages designed to get more quote requests, bookings and contacts. Every element is studied to maximize conversions.",
          details: [
            "Perfect for: Google Ads, Meta Ads campaigns and new service launches.",
          ],
        },
        {
          name: "SEO & Local SEO",
          description: "I help your business appear on Google when customers search for your services. I optimize your website and local presence to increase traffic and inquiries.",
          details: [
            "Goal: more visibility, more customers.",
          ],
        },
        {
          name: "Google Business Profile",
          description: "I optimize your Google Business Profile to improve local search ranking, increase calls and receive more reviews.",
          details: [
            "Ideal for local businesses and professionals.",
          ],
        },
        {
          name: "AI Chatbot for Your Website",
          description: "Provide instant answers to visitors with an intelligent chatbot that improves user experience and reduces support time.",
          details: [],
        },
        {
          name: "Online Bookings",
          description: "I implement simple and intuitive booking systems that allow customers to schedule appointments anytime, without phone calls or emails.",
          details: [],
        },
        {
          name: "Business Automations",
          description: "I automate repetitive tasks like email sending and appointment reminders.",
          details: [
            "More efficiency. Less manual work.",
          ],
        },
        {
          name: "Email Marketing Automation",
          description: "I create automated flows to welcome new customers, send reminders, newsletters and personalized campaigns that increase engagement.",
          details: [],
        },
        {
          name: "Analytics & Dashboard",
          description: "I monitor visits, inquiries, conversions and website performance with easy-to-read dashboards, so you can make data-driven decisions.",
          details: [],
        },
        {
          name: "Maintenance & Support",
          description: "I keep your website always updated, secure and performing with automatic backups, updates, monitoring and continuous support.",
          details: [],
        },
      ],
    },
    testimonials: {
      title: "Real Results from Real Clients",
      subtitle: "Here's what clients say about working together",
      items: [
        {
          content: "Yurii transformed our online presence. Our website now generates 3x more inquiries than before. His attention to detail and strategic approach is unmatched.",
          author: "Marco R.",
          role: "Dental Clinic Owner",
          metric: "3x more inquiries",
        },
        {
          content: "The AI chatbot he implemented for us handles 80% of our customer questions automatically. It saved us 15+ hours per week of manual work.",
          author: "Elena T.",
          role: "E-commerce Business Manager",
          metric: "15+ hours saved weekly",
        },
        {
          content: "Our conversion rate increased by 45% after launching the new landing page. Yurii's data-driven approach really shows results.",
          author: "Giovanni S.",
          role: "Marketing Director",
          metric: "+45% conversions",
        },
        {
          content: "Professional, responsive, and delivers exactly what he promises. Best investment we made for our digital growth this year.",
          author: "Sofia L.",
          role: "Agency Owner",
          metric: "120% ROI",
        },
      ],
    },
    metrics: {
      title: "Impact by Numbers",
      items: [
        {
          label: "Clients Served",
          value: "15+",
          suffix: "",
          description: "Happy businesses growing with digital solutions",
        },
        {
          label: "Hours Saved",
          value: "1000+",
          suffix: "/year",
          description: "through automation & optimization",
        },
        {
          label: "Average Uplift",
          value: "2.8x",
          suffix: "",
          description: "increase in inquiries & conversions",
        },
        {
          label: "Uptime",
          value: "99.9%",
          suffix: "",
          description: "reliability across all deployed solutions",
        },
      ],
    },
    projects: {
      title: "My projects on GitHub",
      view: "View Code",
      info: "Waking up the server... this may take a few seconds.",
      error: "Failed to load repositories",
    },
    contacts: {
      title: "Contact Me",
      description:
        "Send me a message or reach out directly.",
      send: "Send Message",
      sending: "Sending...",
      success: "Message sent successfully!",
      error: "Oops! Something went wrong. Please try again.",
      fields: {
        name: "Your name",
        email: "Your email",
        message: "Describe what you need",
      },
      direct: "Reach me directly via:",
    },
    footer: {
      designedBy: "Designed and made by myself with",
    },
    portfolio: {
      title: "Portfolio",
      intro: "These projects showcase how I help businesses attract customers, increase visibility, and convert visitors into loyal customers. Each solution combines strategic thinking with technical excellence to deliver real, measurable growth.",
      viewProject: "View Project",
      works: [
        {
          id: "clinica-dentistica",
          title: "Clinica Dentistica",
          description: "Professional dental clinic website showcasing services, doctor profiles, and patient testimonials with a modern, clean design to attract new patients and build trust.",
          tags: ["React", "Tailwind CSS", "Next.js"],
          link: "https://dental-clinic-business-growth-websi.vercel.app",
          image: "clinica-dentistica-website-screenshot.jpg",
        },
        {
          id: "bellezza-parrucchieri",
          title: "Bellezza Parrucchieri",
          description: "Modern hair salon website featuring service offerings, stylist profiles, and an elegant design to showcase salon expertise and attract new clients.",
          tags: ["React", "Tailwind CSS", "Next.js"],
          link: "https://beauty-salon-website-lemon.vercel.app",
          image: "beauty-salon-website-screenshot.jpg",
        },
        {
          id: "analytics-dashboard",
          title: "Legal Studio",
          description: "Professional legal services website featuring service listings, attorney profiles, and a modern design to attract and engage potential clients.",
          tags: ["React", "TypeScript", "Chart.js", "Node.js"],
          link: "https://lawyer-website-blush-one.vercel.app/",
          image: "lawyer-website-screenshot.jpg",
        },
        {
          id: "restaurant-website",
          title: "Restaurant Website",
          description: "Modern restaurant website showcasing menu, ambiance, and dining experience with responsive design and smooth user interface.",
          tags: ["React", "Tailwind CSS", "HTML", "CSS"],
          link: "https://restaurant-website-phi-three.vercel.app",
          image: "restaurant-website-screenshot.jpg",
        },
        {
          id: "studio-dentistico",
          title: "Studio Dentistico",
          description: "Professional dental clinic website featuring services, doctor profiles, appointment booking, and patient testimonials with a clean, modern design.",
          tags: ["React", "Tailwind CSS", "Next.js"],
          link: "https://dentist-website-pied.vercel.app/",
          image: "studio-dentistico-website-screenshot.jpg",
        },
        {
          id: "casa-mia-agenzia",
          title: "Casa Mia Agenzia Immobiliare",
          description: "Professional real estate agency website showcasing property listings, agent profiles, and modern design to attract potential buyers and sellers.",
          tags: ["React", "Tailwind CSS", "Next.js"],
          link: "https://agenzia-immobiliare-website.vercel.app",
          image: "agenzia-immobiliare-website-screenshot.jpg",
        },
        {
          id: "girasole-digital",
          title: "Girasole Digital",
          description: "A comprehensive digital marketing platform designed to help businesses manage their online presence, campaigns, and client interactions with intuitive tools and analytics.",
          tags: ["React", "Tailwind CSS", "Node.js", "MongoDB"],
          link: "https://girasole-digital.vercel.app",
          image: "girasole-digital-screenshot.jpg",
        },
      ],
    },
    technologies: {
      title: "Technologies I Work With",
      subtitle: "Modern tools and frameworks for building scalable solutions",
      categories: {
        frontend: {
          name: "Frontend",
          techs: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Redux"],
        },
        backend: {
          name: "Backend",
          techs: ["Node.js", ".NET", "Java", "Spring Boot", "Express"],
        },
        databases: {
          name: "Databases",
          techs: ["PostgreSQL", "MongoDB", "MySQL", "Redis"],
        },
        cloud: {
          name: "Cloud & DevOps",
          techs: ["AWS", "Azure", "Docker", "GitHub Actions", "CI/CD"],
        },
        ai: {
          name: "AI & APIs",
          techs: ["OpenAI", "REST APIs", "GraphQL", "Stripe"],
        },
      },
    },
    howIWork: {
      title: "How I Work",
      subtitle: "A results-focused process from vision to growth",
      steps: [
        {
          number: "1",
          title: "Discovery & Strategy",
          description: "We discuss your business goals, challenges, and growth opportunities to align on the right solution.",
        },
        {
          number: "2",
          title: "Analysis & Planning",
          description: "I analyze your requirements and recommend technical solutions that drive your business goals.",
        },
        {
          number: "3",
          title: "Proposal & Timeline",
          description: "You receive a detailed proposal with scope, deliverables, timeline, and investment.",
        },
        {
          number: "4",
          title: "Development & Updates",
          description: "I build your solution with regular progress updates and transparent communication throughout.",
        },
        {
          number: "5",
          title: "Testing & Optimization",
          description: "Rigorous testing ensures quality, performance, security, and conversion optimization.",
        },
        {
          number: "6",
          title: "Launch & Setup",
          description: "We deploy to production and set up monitoring, analytics, and support systems.",
        },
        {
          number: "7",
          title: "Growth & Support",
          description: "Ongoing optimization, maintenance, and support to ensure your solution keeps delivering results.",
        },
      ],
    },
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Answers to common questions",
      stillQuestions: "Still have questions?",
      getInTouch: "Get in touch",
      items: [
        {
          question: "How much do projects cost?",
          answer: "Project costs vary based on scope, complexity, and timeline. I provide custom quotes after understanding your requirements. Contact me for a free consultation.",
        },
        {
          question: "Do you work remotely?",
          answer: "Yes, I work with clients worldwide. I'm comfortable with remote collaboration using modern tools and maintaining clear communication.",
        },
        {
          question: "Do you provide maintenance and support?",
          answer: "Yes, I offer ongoing maintenance and support packages to ensure your software stays secure, performant, and up-to-date.",
        },
        {
          question: "Can you join existing teams?",
          answer: "Absolutely. I can join your development team, work within your processes, and contribute as a full-time or part-time member.",
        },
        {
          question: "Do you work with startups?",
          answer: "Yes, I love working with startups. I understand the unique challenges and can help you build MVPs and scale efficiently.",
        },
        {
          question: "What technologies do you specialize in?",
          answer: "I specialize in React, Node.js, .NET, TypeScript, and cloud technologies. I'm always learning new tools to best serve my clients.",
        },
      ],
    },
    cookies: {
      title: "Cookie Policy",
      description: "We use cookies to improve your experience, for analytics, and for marketing. You can accept, reject, or manage your preferences. See our",
      privacyLink: "privacy policy",
      manage: "Manage Preferences",
      reject: "Reject All",
      accept: "Accept All",
      necessary: "Necessary cookies (always enabled)",
      analytics: "Analytics cookies",
      marketing: "Marketing cookies",
      save: "Save Preferences",
      close: "Close",
      details: "Cookie Details",
      acceptedText: "Your cookie preferences have been saved.",
    },
    privacy: {
      title: "Privacy Policy",
      lastUpdated: "Last Updated",
      sections: {
        intro: "Introduction",
        cookiePolicy: "Cookie Policy",
        dataCollection: "Data Collection",
        dataUse: "Use of Data",
        dataRights: "Your Rights",
        contact: "Contact Us",
        gdpr: "GDPR Compliance",
      },
      introText: "This privacy policy explains how we collect, use, and protect your personal data when you visit our website. We are committed to complying with GDPR, Italian Privacy Law (GDPR implementation), and all applicable data protection regulations.",
      cookiePolicyText: "We use cookies to improve your browsing experience, provide essential website functionality, and analyze how visitors use our site.",
      necessaryText: "Necessary cookies are required for basic website functions (session management, security, load balancing). These cannot be disabled.",
      analyticsText: "Analytics cookies help us understand how you use our website and improve performance. We use these to gather anonymous usage statistics.",
      marketingText: "Marketing cookies may be used to personalize content and measure the effectiveness of marketing campaigns.",
      consentText: "We obtain explicit consent for non-essential cookies before loading tracking scripts. You can change your preferences at any time by clicking 'Manage Cookies' at the bottom of the page.",
      dataCollectionText: "We collect only the information necessary to provide our services: name and email when you use the contact form, analytics data when cookies are enabled.",
      dataUseText: "Your data is used solely for: responding to your inquiries, improving website functionality, and analyzing usage patterns (analytics only with consent).",
      storageText: "Data is stored securely and retained only as long as necessary. Contact data is retained for 30 days unless you request deletion.",
      dataRightsText: "Under GDPR, you have the right to: access your data, request corrections, request deletion (right to be forgotten), obtain a data portability report, and opt-out of processing. To exercise these rights, contact us at the email below.",
      thirdPartyText: "We do not share your personal data with third parties without your consent, except when required by law.",
      contactUsText: "For privacy inquiries or to exercise your data rights, contact us at: iursuprun@gmail.com",
      gdprText: "We comply with Regulation (EU) 2016/679 (GDPR) and Italian Legislative Decree 196/2003. Our data protection officer is available to assist with compliance questions.",
    },
    terms: {
      title: "Terms of Service",
      lastUpdated: "Last Updated",
      sections: {
        intro: "Introduction",
        serviceDescription: "Service Description",
        useRestrictions: "Use Restrictions",
        intellectualProperty: "Intellectual Property",
        disclaimers: "Disclaimers",
        limitation: "Limitation of Liability",
        governing: "Governing Law",
        contact: "Contact",
      },
      introText: "These Terms of Service govern your use of this website and the services provided. By accessing this site, you agree to these terms.",
      serviceDescriptionText: "This website provides information about software engineering services, portfolio projects, and contact functionality. The services are provided on an 'as-is' basis.",
      acceptableUseText: "You agree not to: use the site for unlawful purposes, transmit malware or harmful code, attempt to gain unauthorized access, or engage in any activity that disrupts the website.",
      emailText: "The contact form is provided for inquiries only. By submitting, you authorize us to respond to your inquiry via email.",
      intellectualPropertyText: "All content on this website (including code, text, images) is the intellectual property of Yurii Suprun unless otherwise stated. You may not reproduce, distribute, or transmit this content without permission.",
      disclaimersText: "The website and services are provided 'as is' without warranties. We do not guarantee uninterrupted or error-free service. Use the website at your own risk.",
      liabilityText: "To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, or consequential damages arising from your use of this website.",
      governingText: "These terms are governed by Italian law and the laws of the European Union. Any disputes shall be resolved under Italian jurisdiction.",
      contactUsText: "For questions about these terms, contact us at: iursuprun@gmail.com",
    },
  },
  it: {
     nav: {
       home: "HOME",
       about: "CHI SONO",
       services: "SERVIZI",
       portfolio: "PORTFOLIO",
       projects: "PROGETTI",
       contacts: "CONTATTI",
       toggleMenuAria: "Apri/chiudi menu",
      switchToItalianAria: "Passa all'italiano",
      switchToEnglishAria: "Passa all'inglese",
      darkModeToLightAria: "Passa alla modalita chiara",
      darkModeToDarkAria: "Passa alla modalita scura",
      localeIt: "IT",
      localeEn: "EN",
      brand: "YURII SUPRUN",
      tagline: "Partner di Crescita Digitale",
    },
    seo: {
      home: {
        title: "Home - Yurii Suprun",
        description:
          "Yurii Suprun - Ingegnere del software, creo soluzioni web interattive e scalabili.",
        keywords: "software engineer, portfolio, Yurii Suprun, web development",
       },
       about: {
         title: "Chi Sono - Yurii Suprun",
         description:
           "Scopri Yurii Suprun, ingegnere del software esperto in sviluppo full-stack e tecnologie cloud.",
         keywords: "about, software engineer, Yurii Suprun, portfolio",
       },
       services: {
         title: "Servizi - Yurii Suprun",
         description:
           "Esplora i servizi che offro incluso sviluppo software, consulenza e soluzioni tecniche.",
         keywords: "servizi, sviluppo software, consulenza, Yurii Suprun",
       },
       projects: {
        title: "Progetti - Yurii Suprun",
        description:
          "Esplora i progetti di Yurii Suprun, inclusi repository GitHub e lavori di sviluppo web.",
        keywords: "projects, GitHub, software development, Yurii Suprun",
      },
      contacts: {
        title: "Contatti - Yurii Suprun",
        description:
          "Contatta Yurii Suprun per collaborazioni o richieste di progetto.",
        keywords: "contact, email, Yurii Suprun, collaboration",
      },
      portfolio: {
        title: "Portfolio - Yurii Suprun",
        description:
          "Esplora i lavori selezionati e i progetti che mostrano la mia esperienza nello sviluppo full-stack e nelle soluzioni tecniche.",
        keywords: "portfolio, progetti, sviluppo software, Yurii Suprun",
      },
      privacy: {
        title: "Privacy Policy - Yurii Suprun",
        description: "Informazioni sull'uso dei cookie e sulle preferenze privacy.",
        keywords: "privacy policy, cookie, Yurii Suprun",
      },
    },
    hero: {
      headlineLines: ["Costruisci la Tua", "Presenza Digitale. Cresci."],
      headline: "Costruisci la Tua Presenza Digitale. Cresci.",
      subtitle: "Aiuto le aziende a creare soluzioni digitali che attirano clienti, aumentano la visibilità e generano risultati misurabili. Dall'automazione con IA ai siti ad alta conversione.",
      ctaProjects: "Vedi Portfolio",
      ctaContact: "Raccontami il Tuo Progetto",
      
      // Social Proof
      experience: "8+ Anni di Esperienza",
      projects: "15+ Progetti Completati",
      industries: "Startup e PMI",
      
      // Trust indicators
      // Legacy keys kept for backwards-compat if referenced elsewhere.
      rolePrefix: "Ciao, sono Yurii, un ingegnere del software",
      roleSuffix: " che crea ",
      typewriterWords: ["interattive", "scalabili", "coinvolgenti"],
      roleSuffixEnd: "soluzioni",
      imageAlt: "Yurii Suprun - Software Engineer",
      links: {
        linkedin: "LinkedIn",
        github: "GitHub",
        email: "iursuprun@gmail.com",
      },
    },
    featured: {
      kicker: "PROGETTI IN EVIDENZA",
      title: "Cose che ho creato",
      analytics: {
        title: "Legal Studio",
        description:
          "Sito professionale che presenta i servizi legali con design moderno e interfaccia incentrata sul cliente.",
        tags: ["React", "TypeScript", "Chart.js"],
      },
      task: {
        title: "Task Manager",
        description:
          "App di produttivita per organizzare i task, impostare priorita e tracciare i progressi.",
        tags: ["Next.js", "Tailwind CSS", "Prisma"],
      },
      ecommerce: {
        title: "Piattaforma E-commerce",
        description:
          "Piattaforma e-commerce full-stack con catalogo, carrello e pagamenti.",
        tags: ["Node.js", "Express", "PostgreSQL"],
      },
    },
    about: {
      title: "Chi Sono",
      description: [
        "Sono Yurii, un Partner di Crescita Digitale specializzato nell'aiutare le aziende a costruire una potente presenza digitale che attrae clienti e guida una crescita misurata e duratura.",
        "Combino il pensiero strategico con l'expertise tecnica per creare soluzioni digitali che non sono solo tecnicamente valide, ma progettate per far crescere la tua azienda. Che si tratti di aumentare la visibilità con SEO, generare richieste qualificate con siti ad alta conversione, catturare lead con assistenti IA, o automatizzare compiti ripetitivi, mi concentro su risultati che contano davvero.",
        "Il mio approccio è collaborativo e focalizzato sui risultati. Lavoro a stretto contatto con te per comprendere gli obiettivi della tua azienda, quindi architetto soluzioni che generano valore reale—sia che si tratti di un sito professionale, di un chatbot alimentato da IA, di una landing page ad alta conversione, o di una trasformazione digitale completa.",
       ],
     },
     services: {
       title: "Servizi",
       description: "Soluzioni orientate ai risultati",
       items: [
         {
           name: "Assistenti AI Vocali",
           description: "Creo assistenti AI vocali capaci di rispondere alle domande dei clienti, raccogliere contatti, qualificare lead, prenotare appuntamenti e fornire assistenza 24 ore su 24.",
           details: [
             "Lavora anche quando il tuo ufficio è chiuso.",
           ],
         },
         {
           name: "Siti Web Professionali",
           description: "Realizzo siti web moderni, veloci e responsive, progettati per trasformare i visitatori in clienti. Ogni progetto è ottimizzato per offrire un'esperienza eccellente su desktop, tablet e smartphone.",
           details: [
             "Ideale per: aziende, professionisti, negozi e attività locali.",
           ],
         },
         {
           name: "Landing Page ad Alta Conversione",
           description: "Creo landing page progettate per ottenere più richieste di preventivo, prenotazioni e contatti. Ogni elemento è studiato per massimizzare le conversioni.",
           details: [
             "Perfette per: campagne Google Ads, Meta Ads e lancio di nuovi servizi.",
           ],
         },
         {
           name: "SEO & Local SEO",
           description: "Aiuto la tua azienda a comparire su Google quando i clienti cercano i tuoi servizi. Ottimizzo il sito e la tua presenza locale per aumentare traffico e richieste.",
           details: [
             "Obiettivo: più visibilità, più clienti.",
           ],
         },
         {
           name: "Google Business Profile",
           description: "Ottimizzo la tua scheda Google Business Profile per migliorare il posizionamento nelle ricerche locali, aumentare le chiamate e ricevere più recensioni.",
           details: [
             "Ideale per attività locali e professionisti.",
           ],
         },
         {
           name: "AI Chatbot per il Sito Web",
           description: "Offri risposte immediate ai visitatori con un chatbot intelligente che migliora l'esperienza utente e riduce il tempo dedicato all'assistenza.",
           details: [],
         },
         {
           name: "Prenotazioni Online",
           description: "Implemento sistemi di prenotazione semplici e intuitivi che permettono ai clienti di fissare appuntamenti in qualsiasi momento, senza telefonate o email.",
           details: [],
         },
         {
           name: "Automazioni Aziendali",
           description: "Automatizzo attività ripetitive come invio email e promemoria appuntamenti.",
           details: [
             "Più efficienza. Meno lavoro manuale.",
           ],
         },
         {
           name: "Email Marketing Automation",
           description: "Creo flussi automatici per accogliere nuovi clienti, inviare promemoria, newsletter e campagne personalizzate che aumentano il coinvolgimento.",
           details: [],
         },
         {
           name: "Analytics & Dashboard",
           description: "Monitoro visite, richieste, conversioni e prestazioni del sito con dashboard semplici da consultare, così puoi prendere decisioni basate sui dati.",
           details: [],
         },
         {
           name: "Manutenzione e Assistenza",
           description: "Mantengo il tuo sito sempre aggiornato, sicuro e performante con backup automatici, aggiornamenti, monitoraggio e supporto continuo.",
           details: [],
         },
       ],
     },
     testimonials: {
      title: "Risultati Reali da Veri Clienti",
      subtitle: "Ecco cosa dicono i clienti sulla collaborazione",
      items: [
        {
          content: "Yurii ha trasformato la nostra presenza online. Il nostro sito ora genera 3 volte più richieste di prima. La sua attenzione ai dettagli e l'approccio strategico sono ineguagliabili.",
          author: "Marco R.",
          role: "Proprietario Clinica Dentistica",
          metric: "3x più richieste",
        },
        {
          content: "Il chatbot IA che ha implementato per noi gestisce automaticamente l'80% delle domande dei clienti. Ci ha fatto risparmiare 15+ ore di lavoro manuale a settimana.",
          author: "Elena T.",
          role: "Responsabile E-commerce",
          metric: "15+ ore risparmiato/settimana",
        },
        {
          content: "Il nostro tasso di conversione è aumentato del 45% dopo il lancio della nuova landing page. L'approccio data-driven di Yurii dimostra davvero risultati.",
          author: "Giovanni S.",
          role: "Direttore Marketing",
          metric: "+45% conversioni",
        },
        {
          content: "Professionale, disponibile e consegna esattamente quello che promette. Il miglior investimento che abbiamo fatto per la nostra crescita digitale quest'anno.",
          author: "Sofia L.",
          role: "Proprietaria Agenzia",
          metric: "120% ROI",
        },
      ],
    },
    metrics: {
      title: "Impatto per Numeri",
      items: [
        {
          label: "Clienti Serviti",
          value: "15+",
          suffix: "",
          description: "aziende felici in crescita con soluzioni digitali",
        },
        {
          label: "Ore Rispariate",
          value: "1000+",
          suffix: "/anno",
          description: "attraverso automazione e ottimizzazione",
        },
        {
          label: "Incremento Medio",
          value: "2.8x",
          suffix: "",
          description: "aumento di richieste e conversioni",
        },
        {
          label: "Uptime",
          value: "99.9%",
          suffix: "",
          description: "affidabilità su tutte le soluzioni implementate",
        },
      ],
    },
    projects: {
      title: "I miei progetti su GitHub",
      view: "Vedi Codice",
      info: "Avvio del server in corso... potrebbero volerci alcuni secondi.",
      error: "Caricamento repository fallito",
    },
    contacts: {
      title: "Contattami",
      description:
        "Inviami un messaggio o contattami direttamente.",
      send: "Invia Messaggio",
      sending: "Invio...",
      success: "Messaggio inviato con successo!",
      error: "Ops! Qualcosa e andato storto. Riprova.",
      fields: {
        name: "Il tuo nome",
        email: "La tua email",
        message: "Descrivi cosa hai bisogno",
      },
      direct: "Contattami direttamente tramite:",
    },
    footer: {
      designedBy: "Progettato e realizzato da me stesso con",
    },
    portfolio: {
      title: "Portfolio",
      intro: "Questi progetti mostrano come aiuto le aziende ad attrarre clienti, aumentare la visibilità e convertire i visitatori in clienti fedeli. Ogni soluzione combina il pensiero strategico con l'eccellenza tecnica per generare crescita reale e misurabile.",
      viewProject: "Vedi Progetto",
      works: [
        {
          id: "clinica-dentistica",
          title: "Clinica Dentistica",
          description: "Sito web professionale di una clinica dentistica con presentazione dei servizi, profili dei dentisti e testimonianze dei pazienti con design moderno e pulito per attirare nuovi pazienti e costruire fiducia.",
          tags: ["React", "Tailwind CSS", "Next.js"],
          link: "https://dental-clinic-business-growth-websi.vercel.app",
          image: "clinica-dentistica-website-screenshot.jpg",
        },
        {
          id: "bellezza-parrucchieri",
          title: "Bellezza Parrucchieri",
          description: "Moderno sito web di un salone di parrucchiera con presentazione dei servizi, profili degli stilisti e design elegante per mettere in mostra l'esperienza del salone e attirare nuovi clienti.",
          tags: ["React", "Tailwind CSS", "Next.js"],
          link: "https://beauty-salon-website-lemon.vercel.app",
          image: "beauty-salon-website-screenshot.jpg",
        },
        {
          id: "analytics-dashboard",
          title: "Legal Studio",
          description: "Sito web di servizi legali professionali con elenco di servizi, profili degli avvocati e design moderno per attrarre e coinvolgere i potenziali clienti.",
          tags: ["React", "TypeScript", "Chart.js", "Node.js"],
          link: "https://lawyer-website-blush-one.vercel.app/",
          image: "lawyer-website-screenshot.jpg",
        },
        {
          id: "restaurant-website",
          title: "Ristorante",
          description: "Moderno sito web di ristorante con esposizione del menu, atmosfera e esperienza culinaria con design responsivo e interfaccia fluida.",
          tags: ["React", "Tailwind CSS", "HTML", "CSS"],
          link: "https://restaurant-website-phi-three.vercel.app",
          image: "restaurant-website-screenshot.jpg",
        },
        {
          id: "studio-dentistico",
          title: "Studio Dentistico",
          description: "Sito web professionale di uno studio dentistico con elenco servizi, profili dei dentisti, prenotazione appuntamenti e testimonianze dei pazienti con design moderno e pulito.",
          tags: ["React", "Tailwind CSS", "Next.js"],
          link: "https://dentist-website-pied.vercel.app/",
          image: "studio-dentistico-website-screenshot.jpg",
        },
        {
          id: "casa-mia-agenzia",
          title: "Casa Mia Agenzia Immobiliare",
          description: "Sito web professionale di un'agenzia immobiliare con elenco proprietà, profili degli agenti e design moderno per attrarre potenziali acquirenti e venditori.",
          tags: ["React", "Tailwind CSS", "Next.js"],
          link: "https://agenzia-immobiliare-website.vercel.app",
          image: "agenzia-immobiliare-website-screenshot.jpg",
        },
        {
          id: "girasole-digital",
          title: "Girasole Digital",
          description: "Una piattaforma completa di digital marketing progettata per aiutare le aziende a gestire la loro presenza online, campagne e interazioni con i clienti tramite strumenti intuitivi e analitiche.",
          tags: ["React", "Tailwind CSS", "Node.js", "MongoDB"],
          link: "https://girasole-digital.vercel.app",
          image: "girasole-digital-screenshot.jpg",
        },
      ],
    },
    technologies: {
      title: "Tecnologie Con Cui Lavoro",
      subtitle: "Strumenti e framework moderni per creare soluzioni scalabili",
      categories: {
        frontend: {
          name: "Frontend",
          techs: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Redux"],
        },
        backend: {
          name: "Backend",
          techs: ["Node.js", ".NET", "Java", "Spring Boot", "Express"],
        },
        databases: {
          name: "Database",
          techs: ["PostgreSQL", "MongoDB", "MySQL", "Redis"],
        },
        cloud: {
          name: "Cloud e DevOps",
          techs: ["AWS", "Azure", "Docker", "GitHub Actions", "CI/CD"],
        },
        ai: {
          name: "IA e API",
          techs: ["OpenAI", "API REST", "GraphQL", "Stripe"],
        },
      },
    },
    howIWork: {
      title: "Come Lavoro",
      subtitle: "Un processo orientato ai risultati, da idea a crescita",
      steps: [
        {
          number: "1",
          title: "Discovery & Strategia",
          description: "Discutiamo i tuoi obiettivi di business, le sfide e le opportunità di crescita.",
        },
        {
          number: "2",
          title: "Analisi e Pianificazione",
          description: "Analizzo i requisiti e propongo soluzioni tecniche che guidano i tuoi obiettivi di business.",
        },
        {
          number: "3",
          title: "Proposta e Timeline",
          description: "Ricevi una proposta dettagliata con scope, deliverables, timeline e investimento.",
        },
        {
          number: "4",
          title: "Sviluppo e Aggiornamenti",
          description: "Sviluppo la tua soluzione con aggiornamenti regolari sul progresso e comunicazione trasparente.",
        },
        {
          number: "5",
          title: "Testing e Ottimizzazione",
          description: "Test rigorosi per garantire qualita, performance, sicurezza e ottimizzazione della conversione.",
        },
        {
          number: "6",
          title: "Launch e Setup",
          description: "Distribuiamo in produzione e configuriamo monitoraggio, analytics e sistemi di supporto.",
        },
        {
          number: "7",
          title: "Crescita e Supporto",
          description: "Ottimizzazione continua, manutenzione e supporto per garantire risultati nel tempo.",
        },
      ],
    },
    faq: {
      title: "Domande Frequenti",
      subtitle: "Risposte alle domande piu comuni",
      stillQuestions: "Hai ancora domande?",
      getInTouch: "Contattami",
      items: [
        {
          question: "Quanto costano i progetti?",
          answer: "I costi del progetto variano in base all'ambito, alla complessita e alle tempistiche. Fornisco preventivi personalizzati dopo aver compreso i tuoi requisiti. Contattami per una consulenza gratuita.",
        },
        {
          question: "Lavori da remoto?",
          answer: "Si, lavoro con clienti in tutto il mondo. Sono comodo con la collaborazione remota utilizzando strumenti moderni e mantenendo una comunicazione chiara.",
        },
        {
          question: "Fornisci manutenzione e supporto?",
          answer: "Si, offro pacchetti di manutenzione e supporto continuo per garantire che il tuo software rimanga sicuro, performante e aggiornato.",
        },
        {
          question: "Puoi unirti ai team esistenti?",
          answer: "Assolutamente. Posso unirti al tuo team di sviluppo, lavorare all'interno dei tuoi processi e contribuire come membro a tempo pieno o part-time.",
        },
        {
          question: "Lavori con startup?",
          answer: "Si, amo lavorare con le startup. Comprendo le sfide uniche e posso aiutarti a creare MVP e scalare in modo efficiente.",
        },
        {
          question: "In quali tecnologie ti specializzi?",
          answer: "Mi specializzo in React, Node.js, .NET, TypeScript e tecnologie cloud. Sto sempre imparando nuovi strumenti per servire al meglio i miei clienti.",
        },
      ],
    },
    cookies: {
      title: "Cookie Policy",
      description: "Usiamo i cookie per migliorare la tua esperienza, per analisi e marketing. Puoi accettare, rifiutare o gestire le preferenze. Leggi la nostra",
      privacyLink: "privacy policy",
      manage: "Gestisci Preferenze",
      reject: "Rifiuta Tutto",
      accept: "Accetta Tutto",
      necessary: "Cookie necessari (sempre attivi)",
      analytics: "Cookie di analisi",
      marketing: "Cookie di marketing",
      save: "Salva Preferenze",
      close: "Chiudi",
      details: "Dettagli Cookie",
      acceptedText: "Le tue preferenze sui cookie sono state salvate.",
    },
    privacy: {
      title: "Privacy Policy",
      lastUpdated: "Ultimo Aggiornamento",
      sections: {
        intro: "Introduzione",
        cookiePolicy: "Cookie Policy",
        dataCollection: "Raccolta dei Dati",
        dataUse: "Uso dei Dati",
        dataRights: "I Tuoi Diritti",
        contact: "Contatti",
        gdpr: "Conformità GDPR",
      },
      introText: "Questa privacy policy spiega come raccogliamo, utilizziamo e proteggiamo i tuoi dati personali quando visiti il nostro sito. Siamo impegnati nel rispetto del GDPR, della legge italiana sulla privacy e di tutte le normative applicabili sulla protezione dei dati.",
      cookiePolicyText: "Utilizziamo i cookie per migliorare la tua esperienza di navigazione, fornire funzionalità essenziali e analizzare come i visitatori utilizzano il nostro sito.",
      necessaryText: "I cookie necessari sono richiesti per le funzioni di base del sito (gestione sessioni, sicurezza, bilanciamento del carico). Questi non possono essere disabilitati.",
      analyticsText: "I cookie di analisi ci aiutano a capire come utilizzi il nostro sito e a migliorare le prestazioni. Li utilizziamo per raccogliere statistiche di utilizzo anonime.",
      marketingText: "I cookie di marketing possono essere utilizzati per personalizzare i contenuti e misurare l'efficacia delle campagne di marketing.",
      consentText: "Otteniamo il consenso esplicito per i cookie non essenziali prima di caricare script di tracciamento. Puoi cambiare le tue preferenze in qualsiasi momento facendo clic su 'Gestisci Cookie' in fondo alla pagina.",
      dataCollectionText: "Raccogliamo solo le informazioni necessarie per fornire i nostri servizi: nome e email quando utilizzi il modulo di contatto, dati di analisi quando i cookie sono abilitati.",
      dataUseText: "I tuoi dati vengono utilizzati esclusivamente per: rispondere alle tue richieste, migliorare la funzionalità del sito e analizzare i modelli di utilizzo (solo analisi con consenso).",
      storageText: "I dati sono archiviati in modo sicuro e conservati solo il tempo necessario. I dati di contatto vengono conservati per 30 giorni a meno che non richieda l'eliminazione.",
      dataRightsText: "Secondo il GDPR, hai il diritto di: accedere ai tuoi dati, richiedere correzioni, richiedere l'eliminazione (diritto all'oblio), ottenere un rapporto di portabilità dei dati e rinunciare all'elaborazione. Per esercitare questi diritti, contattaci all'email sottostante.",
      thirdPartyText: "Non condividiamo i tuoi dati personali con terze parti senza il tuo consenso, salvo quando richiesto dalla legge.",
      contactUsText: "Per domande sulla privacy o per esercitare i tuoi diritti relativi ai dati, contattaci a: iursuprun@gmail.com",
      gdprText: "Siamo conformi al Regolamento (UE) 2016/679 (GDPR) e al Decreto Legislativo italiano 196/2003. Il nostro responsabile della protezione dei dati è disponibile per assistere con domande sulla conformità.",
    },
    terms: {
      title: "Termini di Servizio",
      lastUpdated: "Ultimo Aggiornamento",
      sections: {
        intro: "Introduzione",
        serviceDescription: "Descrizione del Servizio",
        useRestrictions: "Restrizioni d'Uso",
        intellectualProperty: "Proprietà Intellettuale",
        disclaimers: "Esclusioni di Responsabilità",
        limitation: "Limitazione della Responsabilità",
        governing: "Legge Applicabile",
        contact: "Contatti",
      },
      introText: "Questi Termini di Servizio disciplinano l'uso di questo sito e dei servizi forniti. Accedendo al sito, accetti questi termini.",
      serviceDescriptionText: "Questo sito fornisce informazioni su servizi di ingegneria del software, progetti di portfolio e funzionalità di contatto. I servizi sono forniti su base 'così come sono'.",
      acceptableUseText: "Accetti di non: utilizzare il sito per scopi illegittimi, trasmettere malware o codice dannoso, tentare di ottenere accesso non autorizzato o impegnarti in qualsiasi attività che interrompa il sito.",
      emailText: "Il modulo di contatto è fornito solo per le richieste di informazioni. Inviando, autorizzi a rispondere alla tua richiesta via email.",
      intellectualPropertyText: "Tutti i contenuti su questo sito (incluso codice, testo, immagini) sono proprietà intellettuale di Yurii Suprun se non diversamente indicato. Non puoi riprodurre, distribuire o trasmettere questi contenuti senza permesso.",
      disclaimersText: "Il sito e i servizi sono forniti 'così come sono' senza garanzie. Non garantiamo un servizio ininterrotto o senza errori. Utilizza il sito a tuo rischio.",
      liabilityText: "Nella massima misura consentita dalla legge, non saremo responsabili per eventuali danni indiretti, incidentali o consequenziali derivanti dall'uso di questo sito.",
      governingText: "Questi termini sono disciplinati dalla legge italiana e dalle leggi dell'Unione Europea. Qualsiasi controversia sarà risolta secondo la giurisdizione italiana.",
      contactUsText: "Per domande su questi termini, contattaci a: iursuprun@gmail.com",
    },
  },
};

